import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './RecentlyDeleted.css';

const RecentlyDeleted = () => {
  const { user } = useAuth();
  const [deletedForms, setDeletedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchDeletedForms();
  }, []);

  const fetchDeletedForms = async () => {
    try {
      console.log('Fetching deleted forms for district admin...');
      const response = await axios.get('/api/form-submissions/district-deleted-forms');
      console.log('Deleted forms response:', response.data);
      setDeletedForms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted forms:', error);
      alert(`Failed to load deleted forms: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (item) => {
    try {
      if (item.type === 'student' || item.type === 'student_intake') {
        // Note: District admins can't restore students or student intake, only parents can
        alert(`${item.type === 'student' ? 'Students' : 'Student intake records'} can only be restored by their parent`);
        setRestoreConfirm(null);
        return;
      }
      await axios.post(`/api/form-submissions/restore-form/${item.id}`);
      setDeletedForms(deletedForms.filter(f => f.id !== item.id));
      setRestoreConfirm(null);
      alert('Form restored successfully');
    } catch (error) {
      console.error('Failed to restore form:', error);
      alert('Failed to restore form');
    }
  };

  const handlePermanentDelete = async (item) => {
    try {
      if (item.type === 'student') {
        await axios.delete(`/api/form-submissions/district-permanent-delete-student/${item.id}`);
        alert('Student permanently deleted');
      } else if (item.type === 'student_intake') {
        await axios.delete(`/api/form-submissions/district-permanent-delete-student-intake/${item.id}`);
        alert('Student intake permanently deleted');
      } else {
        await axios.delete(`/api/form-submissions/district-permanent-delete/${item.id}`);
        alert('Form permanently deleted');
      }
      setDeletedForms(deletedForms.filter(f => f.id !== item.id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to permanently delete:', error);
      if (error.response?.status === 404) {
        // Record was already deleted, remove from UI anyway
        alert('Record was already permanently deleted');
        setDeletedForms(deletedForms.filter(f => f.id !== item.id));
        setDeleteConfirm(null);
      } else {
        alert(`Failed to permanently delete: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading deleted forms...</div>;
  }

  return (
    <div className="recently-deleted">
      <div className="page-header">
        <h1>Recently Deleted</h1>
        <p>Forms and students that have been deleted in your district in the last 7 days. They will be permanently deleted after 7 days.</p>
      </div>

      {restoreConfirm && (
        <div className="modal-overlay" onClick={() => setRestoreConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Restore Form</h3>
            <p>Are you sure you want to restore this form? It will be available again for the parent.</p>
            <div className="modal-actions">
              <button onClick={() => handleRestore(restoreConfirm)} className="btn-primary">Restore</button>
              <button onClick={() => setRestoreConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Permanently Delete {deleteConfirm.type === 'student' ? 'Student' : 'Form'}</h3>
            <p><strong>Warning:</strong> This action cannot be undone. The {deleteConfirm.type === 'student' ? 'student' : 'form'} will be permanently deleted from the database.</p>
            <div className="modal-actions">
              <button onClick={() => handlePermanentDelete(deleteConfirm)} className="btn-danger">Permanently Delete</button>
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="deleted-forms-list">
        {deletedForms.length === 0 ? (
          <div className="no-deleted-forms">
            <p>No recently deleted forms found in your district.</p>
          </div>
        ) : (
          <div className="forms-grid">
            {deletedForms.map(item => (
              <div key={item.id} className="form-card deleted">
                <div className="form-header">
                  <h3>{item.type === 'student' ? 'Student Record' : item.type === 'student_intake' ? 'Student Intake Record' : (item.instance_name || item.form_name)}</h3>
                  <span className="student-name">{item.student_first_name} {item.student_last_name}</span>
                  {item.type === 'student' && item.grade && (
                    <span className="grade-badge">Grade {item.grade}</span>
                  )}
                </div>
                <div className="form-meta">
                  <p><strong>Parent:</strong> {item.parent_first_name} {item.parent_last_name}</p>
                  <p><strong>Deleted:</strong> {new Date(item.deleted_at).toLocaleDateString()}</p>
                  <p><strong>Created:</strong> {new Date(item.created_at).toLocaleDateString()}</p>
                  {item.type !== 'student' && item.type !== 'student_intake' && (
                    <p><strong>Form Data:</strong> {item.form_data ? Object.keys(item.form_data).length : 0} fields</p>
                  )}
                  {item.type === 'student' && (
                    <p><strong>Type:</strong> Student Record</p>
                  )}
                  {item.type === 'student_intake' && (
                    <p><strong>Type:</strong> Student Intake Record</p>
                  )}
                </div>
                <div className="form-actions">
                  {item.type !== 'student' && item.type !== 'student_intake' && (
                    <button
                      onClick={() => setRestoreConfirm(item)}
                      className="btn-primary"
                    >
                      Restore Form
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(item)}
                    className="btn-danger"
                  >
                    Delete Forever
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentlyDeleted;
